package one.healthium.onboarding.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.first
import one.healthium.onboarding.domain.models.Slider
import one.healthium.ui.compose.components.AppAlertDialog
import one.healthium.ui.compose.components.AppLinearProgressIndicator
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import kotlin.time.Duration.Companion.seconds

@Composable
internal fun ProgressSlider(
    modifier: Modifier = Modifier,
    slider: Slider?,
    small: Boolean = false,
    autoStart: Boolean = true,
    onFinished: () -> Unit,
) {
    if (slider == null) return

    val showPopup = remember { MutableStateFlow<PopupUiModel?>(null) }
    val progress = remember { Animatable(0f) }

    LaunchedEffect(autoStart) {
        if (autoStart) {
            slider.intervals.fastForEach { interval ->
                progress.animateTo(
                    targetValue = interval.percentage / 100f,
                    animationSpec = tween(
                        durationMillis = interval.timeInterval.seconds.inWholeMilliseconds.toInt(),
                        easing = LinearEasing,
                    )
                )
                val popupMessage = interval.popupMessage
                if (!popupMessage.isNullOrBlank()) {
                    showPopup.value = PopupUiModel(
                        message = popupMessage,
                        positiveButton = interval.answers?.getOrNull(1),
                        negativeButton = interval.answers?.getOrNull(0),
                    )
                    showPopup.first { it == null } // wait for dialog completion
                }
            }
            delay(1000)
            onFinished()
        }
    }

    PopUp(showPopup)

    Column(
        modifier = modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AppText(
                modifier = Modifier.weight(1f),
                text = slider.title,
                style = if (progress.value > 0) AppTheme.textStyles.body1Medium else AppTheme.textStyles.body1,
                color = if (progress.value > 0) AppTheme.colors.text100 else gray_3,
            )
            Text(
                text = "${(progress.value * 100).toInt()}%",
                style = AppTheme.textStyles.body1,
            )
        }
        Spacer(Modifier.height(6.dp))
        AppLinearProgressIndicator(
            modifier = Modifier
                .height(if (small) 6.dp else 16.dp)
                .fillMaxWidth(),
            progress = { progress.value }
        )
    }
}

@Composable
private fun PopUp(showPopup: MutableStateFlow<PopupUiModel?>) {
    val popUp by showPopup.collectAsState()
    val hapticFeedback = LocalHapticFeedback.current
    popUp?.let { uiModel ->
        LaunchedEffect(uiModel) {
            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
        }
        AppAlertDialog(
            positiveButton = uiModel.positiveButton ?: "Ok",
            negativeButton = uiModel.negativeButton,
            text = uiModel.message,
            onPositiveClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
            },
            onNegativeClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
            },
            onDismissRequest = {
                showPopup.value = null
            },
        )
    }
}

private data class PopupUiModel(
    val message: String,
    val positiveButton: String?,
    val negativeButton: String?,
)
